import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, CheckCircle2, XCircle } from "lucide-react";
import Navbar from "@/components/site/Navbar";
import Footer, { ChatBubble } from "@/components/site/Footer";
import { SiteProvider } from "@/context/SiteContext";
import { api, formatApiErrorDetail, trackVisit } from "@/lib/api";

const ROLES = [
  "E-commerce Support",
  "Real Estate Operations",
  "Appointment Setting",
  "Cold Calling & Phone Support",
  "Administrative & Executive Assistance",
  "Social Media Management & Content",
  "General Application",
];

function Apply() {
  const [questions, setQuestions] = useState([]);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [phase, setPhase] = useState("loading");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ name: "", email: "", phone: "", role: "", years: "", bio: "" });

  useEffect(() => {
    trackVisit();
    api.get("/recruitment/questions")
      .then(({ data }) => {
        setQuestions(data);
        setPhase(data.length > 0 ? "questions" : "form");
      })
      .catch(() => setPhase("form"));
  }, []);

  const pick = async (optionIndex) => {
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      const q = questions[idx];
      const { data } = await api.post("/apply/check", { question_id: q.id, option_index: optionIndex });
      if (data.passed) {
        const next = [...answers, { question_id: q.id, option_index: optionIndex }];
        setAnswers(next);
        if (idx + 1 < questions.length) setIdx(idx + 1);
        else setPhase("form");
      } else {
        setPhase("rejected");
      }
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    setError("");
    if (!form.name || !form.email) { setError("Please fill in your name and email."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/apply", { ...form, answers });
      setPhase(data.passed ? "success" : "rejected");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-[#0A1628] min-h-screen">
      <Navbar />

      <section className="relative overflow-hidden bg-dark-premium pt-[150px] pb-16 lg:pt-[170px]">
        <div className="relative max-w-[1400px] mx-auto px-5 lg:px-10">
          <div className="eyebrow text-[#00CFFF] mb-5">Careers at TrustDesk</div>
          <h1 className="font-display font-black text-white text-4xl sm:text-5xl lg:text-[56px] leading-[1.08] tracking-tight max-w-3xl">
            Apply to join <span className="text-[#00CFFF]">the team.</span>
          </h1>
          <p className="mt-5 text-white/85 text-base sm:text-lg max-w-2xl leading-relaxed">
            Answer a few quick questions, and if it's a match, tell us about yourself. Our recruitment team reviews every application personally.
          </p>
        </div>
      </section>

      <section className="py-20 relative bg-dark-premium" data-testid="apply-page">
        <div className="relative max-w-3xl mx-auto px-5">
          <AnimatePresence mode="wait">
            {phase === "loading" && (
              <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="text-center text-slate-400 py-16">
                Loading qualifying questions...
              </motion.div>
            )}

            {phase === "questions" && (
              <motion.div
                key={"q" + idx}
                initial={{ opacity: 0, y: 28 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                className="rounded-3xl glass-dark p-8 sm:p-10"
                data-testid="apply-question-card"
              >
                <div className="flex items-center justify-between">
                  <span className="eyebrow text-[#00CFFF] text-[10px]">Qualifying question {idx + 1} of {questions.length}</span>
                  <div className="flex gap-1.5">
                    {questions.map((_, i) => (
                      <span key={i} className={`h-1.5 rounded-full transition-all duration-300 ${i < idx ? "w-6 bg-[#00CFFF]" : i === idx ? "w-6 bg-[#00CFFF]/60" : "w-4 bg-white/15"}`} />
                    ))}
                  </div>
                </div>
                <h2 className="mt-6 font-display font-extrabold text-white text-2xl leading-snug">{questions[idx].question}</h2>
                <div className="mt-7 space-y-3">
                  {questions[idx].options.map((opt, oi) => (
                    <button
                      key={oi}
                      onClick={() => pick(oi)}
                      disabled={busy}
                      className="w-full text-left rounded-xl border border-white/15 bg-white/[0.04] px-5 py-4 text-white font-semibold hover:border-[#00CFFF] hover:bg-[rgba(0,191,255,0.08)] transition-all duration-200 disabled:opacity-50"
                      data-testid={`apply-option-${idx}-${oi}`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
                {error && <div className="mt-4 text-sm text-red-400">{error}</div>}
              </motion.div>
            )}

            {phase === "rejected" && (
              <motion.div
                key="rejected"
                initial={{ opacity: 0, y: 28 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="rounded-3xl glass-dark p-10 text-center"
                data-testid="apply-rejected"
              >
                <span className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center mx-auto">
                  <XCircle size={30} className="text-slate-300" />
                </span>
                <h2 className="mt-6 font-display font-extrabold text-white text-2xl">Thanks for your time!</h2>
                <p className="mt-3 text-slate-300 leading-relaxed max-w-md mx-auto">
                  Thank you for your interest in TrustDesk! Unfortunately, your answers tell us this role isn't the right match at the moment.
                  We truly appreciate your time — please check back soon, as our needs evolve.
                </p>
                <a href="/" className="btn btn-outline-light mt-8">Return to website</a>
              </motion.div>
            )}

            {phase === "form" && (
              <motion.div
                key="form"
                initial={{ opacity: 0, y: 28 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                className="rounded-3xl glass-dark p-8 sm:p-10"
                data-testid="apply-form"
              >
                <div className="flex items-center gap-2.5">
                  <CheckCircle2 size={20} className="text-emerald-400" />
                  <span className="text-sm font-bold text-emerald-300">You passed all qualifying questions</span>
                </div>
                <h2 className="mt-4 font-display font-extrabold text-white text-2xl">Tell us about yourself</h2>
                <div className="mt-6 grid sm:grid-cols-2 gap-4">
                  <input className="admin-input" placeholder="Full name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="apply-input-name" />
                  <input className="admin-input" type="email" placeholder="Email *" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="apply-input-email" />
                  <input className="admin-input" placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="apply-input-phone" />
                  <select className="admin-input" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="apply-input-role">
                    <option value="">Role applying for *</option>
                    {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                  <input className="admin-input" type="number" min="0" placeholder="Years of experience" value={form.years} onChange={(e) => setForm({ ...form, years: e.target.value })} data-testid="apply-input-years" />
                  <textarea className="admin-input sm:col-span-2" rows={4} placeholder="Short bio — tell us about your experience and strengths" value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} data-testid="apply-input-bio" />
                </div>
                {error && <div className="mt-4 text-sm text-red-400">{error}</div>}
                <button className="btn btn-primary w-full mt-6" onClick={submit} disabled={busy} data-testid="apply-submit-button">
                  {busy ? "Submitting..." : "Submit Application"} <ArrowRight size={16} />
                </button>
              </motion.div>
            )}

            {phase === "success" && (
              <motion.div
                key="success"
                initial={{ opacity: 0, y: 28 }}
                animate={{ opacity: 1, y: 0 }}
                className="rounded-3xl glass-dark p-10 text-center"
                data-testid="apply-success"
              >
                <span className="w-16 h-16 rounded-full bg-emerald-400/15 flex items-center justify-center mx-auto">
                  <CheckCircle2 size={30} className="text-emerald-400" />
                </span>
                <h2 className="mt-6 font-display font-extrabold text-white text-2xl">Application received!</h2>
                <p className="mt-3 text-slate-300 leading-relaxed max-w-md mx-auto">
                  Thanks, {form.name.split(" ")[0]}! Our recruitment team will review your application and reach out within a few days.
                </p>
                <a href="/" className="btn btn-primary mt-8">Back to website</a>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="mt-8">
            <a href="/" className="flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors w-fit">
              <ArrowLeft size={15} /> Back to website
            </a>
          </div>
        </div>
      </section>

      <Footer />
      <ChatBubble />
    </div>
  );
}

export default function ApplyNowPage() {
  return (
    <SiteProvider>
      <Apply />
    </SiteProvider>
  );
}
